import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import useMap from "./useMap";
import { getMatchingKey} from "./objectUtil";
import {layerDefaultOpts} from "./staticvariables";

const useHeatmap = (mapRef) => {

    const {getSource, getLayer, addLayer, removeLayer, removeSource} = useMap(mapRef);

    /**
     * Adds heatmap layer to the map.
     * @param {String} source_id - Source ID for layer to use.
     *
     * @param {Object} heatmap_layer_options - Options for the adding heatmap layer.
     * @param {String} heatmap_layer_options.id - Heatmap layer ID to use.
     * @param {Array} heatmap_layer_options.heatmap_colors - Array of heatmap colors in order from low (left) to high (right), with supported formats include color names, hex colors, and RGB (e.g., ['#2166ac', '#67a9cf']).
     * 
     * @param {Object} circle_layer_options - Options for the adding circle layer.
     * @param {String} circle_layer_options.id - Circle layer ID to use.
     * 
     * @param {Object} camera_options - Options for the camera.
     * @param {Array} camera_options.center - Center coordinates for where to focus layer in map.
     * @param {Number} camera_options.zoom - Zoom scale for layer in map.
     */
    const addHeatmapWithCircleLayer = (source_id, heatmap_layer_options, circle_layer_options, camera_options) => {
        let source = getSource(source_id);
        if (!source) throw new Error("No source found.");
        
        // 1. Add layers
        let heatmap_layer_id = heatmap_layer_options.id
        const heatmapLayer = getLayer(heatmap_layer_id)

        if (!heatmapLayer) {
            createHeatmapLayer(heatmap_layer_id, source_id, heatmap_layer_options.heatmap_colors)
        }

        let circle_layer_id = circle_layer_options.id
        const circleLayer = getLayer(circle_layer_id)

        if (!circleLayer) {
            createCircleLayer(circle_layer_id, source_id)
        }

        // 2. Set camera focus for layer
        let layer_camera_center = camera_options.center || layerDefaultOpts.camera.center
        let layer_camera_zoom = camera_options.zoom || layerDefaultOpts.camera.zoom

        mapRef.current.setCenter(layer_camera_center)
        mapRef.current.setZoom(layer_camera_zoom)
    }

    const createHeatmapLayer = (layer_id, source_id, colors) => {
        const interpolationExpressionHeatmapColor = createInterpolationExpressionForHeatmapColor(colors);

        addLayer({
            id: layer_id,
            type: 'heatmap',
            source: source_id,
            maxzoom: 9,
            paint: {
            //   Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
            //   Begin color ramp at 0-stop with a 0-transparancy color
            //   to create a blur-like effect.
              'heatmap-color': interpolationExpressionHeatmapColor,
              // Adjust the heatmap radius by zoom level
              'heatmap-radius': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                2,
                9,
                20
              ],
            }
          },
          'waterway-label'
        );
    }

    const createInterpolationExpressionForHeatmapColor = (colors) => {
        // Add the default color at the beginning of the colors array
        const defaultColor = 'rgba(33,102,172,0)';
        const extendedColors = [defaultColor, ...colors];

        // Number of colors
        const numColors = extendedColors.length;
        
        // Check if there are at least two colors
        if (numColors < 2) {
            throw new Error("At least two colors are required.");
        }

        // Calculate step size
        const step = 1 / (numColors - 1);
        
        // Create the interpolation expression
        const expression = [
            "interpolate",
            ["linear"],
            ["heatmap-density"]
        ];
        
        // Add colors with calculated stops
        for (let i = 0; i < numColors; i++) {
            expression.push(i * step);
            expression.push(extendedColors[i]);
        }
        
        return expression;
    }

    const createCircleLayer = (layer_id, source_id) => {
        addLayer(
            {
              id: layer_id,
              type: 'circle',
              source: source_id,
              minzoom: 7,
              paint: {
                'circle-color': '#4264fb',
                'circle-radius': 5,
                'circle-stroke-width': 1,
                'circle-stroke-color': 'white'
              }
            },
            'waterway-label'
          );
    }

    /**
     * Get heatmap data with update and remove functions.
     *
     * @param {string} heatmap_layer_id - Heatmap layer ID.
     * @param {string} circle_layer_id - Circle layer ID.
     */
    const getHeatmapWithCircleLayer = (heatmap_layer_id, circle_layer_id) => {
        const heatmap_layer = getLayer(heatmap_layer_id)
        const circle_layer = getLayer(circle_layer_id)

        if (!heatmap_layer && !circle_layer) return null;

        let source_id = heatmap_layer.source
        const source_data = getSource(source_id)

        const updateData = (newData) => {
            if (source_data) source_data.setData(newData);
        };

        const updateHeatmapLayer = (updated_props) => {
            // Update for layout property
            const propKeyInLayout = getMatchingKey(updated_props, heatmap_layer.layout);
            if (propKeyInLayout) {
                mapRef.current.setLayoutProperty(heatmap_layer_id, propKeyInLayout, updated_props[propKeyInLayout]);
            }

            // Update for paint property
            const propKeyInPaint = getMatchingKey(updated_props, heatmap_layer.paint);
            if (propKeyInPaint) {
                mapRef.current.setPaintProperty(heatmap_layer_id, propKeyInLayout, updated_props[propKeyInLayout]);
            }
        }

        const updateCircleLayer = (updated_props) => {
            // Update for layout property
            const propKeyInLayout = getMatchingKey(updated_props, circle_layer.layout);
            if (propKeyInLayout) {
                mapRef.current.setLayoutProperty(circle_layer_id, propKeyInLayout, updated_props[propKeyInLayout]);
            }

            // Update for paint property
            const propKeyInPaint = getMatchingKey(updated_props, circle_layer.paint);
            if (propKeyInPaint) {
                mapRef.current.setPaintProperty(circle_layer_id, propKeyInLayout, updated_props[propKeyInLayout]);
            }
        }

        const removeHeatmap = () => {
            removeLayer(heatmap_layer_id);
            removeLayer(circle_layer_id);
            removeSource(source_id);
        }

        return {
            data: {source_data, heatmap_layer, circle_layer},
            updateData,
            updateHeatmapLayer,
            updateCircleLayer,
            removeHeatmap
        };
    }

    return {addHeatmapWithCircleLayer, getHeatmapWithCircleLayer}
}

export default useHeatmap